/** Cross-tab hint for company product list; full row still comes from API / React Query. */
export const PRODUCTS_CACHE_KEY = "company_products_cache";
const PRODUCTS_CACHE_VERSION = 6;
const PRODUCTS_CACHE_DURATION = 24 * 60 * 60 * 1000;

export type ProductsCachePayload = {
  products: string[];
  companyId: string;
  timestamp: number;
  enablePerUserPermissions?: boolean;
  dateFilterMinStartDate?: string | null;
  dateFilterMaxEndDate?: string | null;
  miContenidoDateFilterMinStartDate?: string | null;
  miContenidoDateFilterMaxEndDate?: string | null;
  trackerBrandId?: string | null;
  explorerSponsorCategoryFilter?: string | null;
  version?: number;
};

export function readCompanyProductsCache(companyId: string): {
  products: string[];
  enablePerUserPermissions: boolean;
  dateFilterMinStartDate?: string | null;
  dateFilterMaxEndDate?: string | null;
  miContenidoDateFilterMinStartDate?: string | null;
  miContenidoDateFilterMaxEndDate?: string | null;
  trackerBrandId?: string | null;
  explorerSponsorCategoryFilter?: string | null;
} | null {
  try {
    const raw = localStorage.getItem(PRODUCTS_CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as ProductsCachePayload;
    if (
      data.companyId === companyId &&
      data.version === PRODUCTS_CACHE_VERSION &&
      Date.now() - data.timestamp < PRODUCTS_CACHE_DURATION
    ) {
      return {
        products: data.products,
        enablePerUserPermissions: data.enablePerUserPermissions || false,
        dateFilterMinStartDate: data.dateFilterMinStartDate ?? null,
        dateFilterMaxEndDate: data.dateFilterMaxEndDate ?? null,
        miContenidoDateFilterMinStartDate:
          data.miContenidoDateFilterMinStartDate ?? null,
        miContenidoDateFilterMaxEndDate:
          data.miContenidoDateFilterMaxEndDate ?? null,
        trackerBrandId: data.trackerBrandId ?? null,
        explorerSponsorCategoryFilter:
          data.explorerSponsorCategoryFilter ?? null,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function writeCompanyProductsCache(
  companyId: string,
  products: string[],
  enablePerUser: boolean,
  dateFilterMinStartDate?: string | null,
  dateFilterMaxEndDate?: string | null,
  miContenidoDateFilterMinStartDate?: string | null,
  miContenidoDateFilterMaxEndDate?: string | null,
  trackerBrandId?: string | null,
  explorerSponsorCategoryFilter?: string | null,
): void {
  const data: ProductsCachePayload = {
    products,
    companyId,
    timestamp: Date.now(),
    enablePerUserPermissions: enablePerUser,
    dateFilterMinStartDate: dateFilterMinStartDate ?? null,
    dateFilterMaxEndDate: dateFilterMaxEndDate ?? null,
    miContenidoDateFilterMinStartDate: miContenidoDateFilterMinStartDate ?? null,
    miContenidoDateFilterMaxEndDate: miContenidoDateFilterMaxEndDate ?? null,
    trackerBrandId: trackerBrandId ?? null,
    explorerSponsorCategoryFilter: explorerSponsorCategoryFilter ?? null,
    version: PRODUCTS_CACHE_VERSION,
  };
  const serialized = JSON.stringify(data);
  try {
    localStorage.setItem(PRODUCTS_CACHE_KEY, serialized);
  } catch {
    try {
      localStorage.removeItem(PRODUCTS_CACHE_KEY);
      localStorage.setItem(PRODUCTS_CACHE_KEY, serialized);
    } catch {
      /* ignore */
    }
  }
}

export function clearCompanyProductsCache(): void {
  try {
    localStorage.removeItem(PRODUCTS_CACHE_KEY);
  } catch {
    /* ignore */
  }
}
